/**
 * QUOTATION FILE SLICE — allegati del preventivo corrente.
 *
 * `items` contiene gli allegati di UN preventivo alla volta: la lista viene
 * sostituita a ogni `fetchQuotationFiles`, e `clearQuotationFiles` la svuota
 * allo smontaggio del dettaglio. Non è un repository globale.
 *
 * La chiave della slice è `quotationFiles` e non `quotationFiles` per la
 * ragione spiegata in `quotation/quotationSlice.ts`: i reducer dei plugin
 * vincono a parità di chiave. Nome definitivo allo step 12 di TS-87 (D11).
 *
 * I case sono scritti a mano invece che con `addCrudCases` perché i thunk non
 * vengono dal factory (risorsa annidata, upload multipart). `byId` viene
 * comunque mantenuto: è la cache stabile per id che `items` non può fare, e
 * lasciarla vuota renderebbe inutile il campo per chiunque arrivi dopo.
 */

import { createSlice } from '@reduxjs/toolkit';
import type { QuotationFileState } from './quotationFileTypes';
import {
  fetchQuotationFiles,
  uploadQuotationFile,
  updateQuotationFile,
  deleteQuotationFile,
} from './quotationFileThunks';

const initialState: QuotationFileState = {
  items: [],
  byId: {},
  currentItem: null,
  status: 'idle',
  error: null,
  totalCount: 0,
};

export const quotationFileSlice = createSlice({
  name: 'quotationFiles',
  initialState,
  reducers: {
    clearQuotationFiles(state) {
      state.items = [];
      state.totalCount = 0;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotationFiles.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchQuotationFiles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        for (const file of action.payload.items) state.byId[file.quotationFileId] = file;
      })
      .addCase(fetchQuotationFiles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      .addCase(uploadQuotationFile.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.byId[action.payload.quotationFileId] = action.payload;
        state.totalCount += 1;
      })
      .addCase(updateQuotationFile.fulfilled, (state, action) => {
        const idx = state.items.findIndex(
          (f) => f.quotationFileId === action.payload.quotationFileId,
        );
        if (idx !== -1) state.items[idx] = action.payload;
        state.byId[action.payload.quotationFileId] = action.payload;
      })
      .addCase(deleteQuotationFile.fulfilled, (state, action) => {
        state.items = state.items.filter((f) => f.quotationFileId !== action.payload);
        delete state.byId[action.payload];
        state.totalCount = Math.max(0, state.totalCount - 1);
      });
  },
});

export const { clearQuotationFiles } = quotationFileSlice.actions;
export const quotationFileReducer = quotationFileSlice.reducer;
